import BaseModal from '@/components/BaseModal';
import MediumText from '@/components/MediumText';
import { openCrisp } from '@/helpers';
import { ModalDynamicThemeListStyled } from '@/pages/GeneralSettings/components/DisplayAndPosition/components/ModalDynamicThemeList/styled';
import { DataTable, Icon, LegacyCard, Link } from '@shopify/polaris';
import { CheckIcon, ExternalIcon } from '@shopify/polaris-icons';
import { useMemo } from 'react';

interface IProps {
  isOpenModal: boolean;
  setIsOpenModal: (value: boolean) => void;
}

type Status = 'compatible' | 'not compatible' | 'same';

const themeList: {
  id: number;
  name: string;
  url: string;
  cartPage?: Status;
  cartDrawer?: Status;
  cartPopup?: Status;
}[] = [
  {
    id: 1,
    name: 'Colorblock',
    url: 'https://themes.shopify.com/themes/colorblock/styles/default',
    cartPage: 'compatible',
    cartDrawer: 'compatible',
    cartPopup: 'not compatible',
  },
  {
    id: 2,
    name: 'Craft',
    url: 'https://themes.shopify.com/themes/craft/styles/default',
    cartPage: 'compatible',
    cartDrawer: 'compatible',
  },
  {
    id: 3,
    name: 'Crave',
    url: 'https://themes.shopify.com/themes/crave/styles/default',
    cartPage: 'compatible',
    cartDrawer: 'compatible',
  },
  {
    id: 4,
    name: 'Dawn',
    url: 'https://themes.shopify.com/themes/dawn/styles/default',
    cartPage: 'compatible',
    cartDrawer: 'compatible',
  },
  {
    id: 5,
    name: 'Origin',
    url: 'https://themes.shopify.com/themes/origin/styles/default',
    cartPage: 'compatible',
    cartDrawer: 'compatible',
  },
  {
    id: 6,
    name: 'Publisher',
    url: 'https://themes.shopify.com/themes/publisher/styles/default',
    cartPage: 'compatible',
    cartDrawer: 'compatible',
  },
  {
    id: 7,
    name: 'Refresh',
    url: 'https://themes.shopify.com/themes/refresh/styles/default',
    cartPage: 'compatible',
    cartDrawer: 'compatible',
  },
  {
    id: 8,
    name: 'Ride',
    url: 'https://themes.shopify.com/themes/ride/styles/default',
    cartPage: 'compatible',
    cartDrawer: 'compatible',
  },
  {
    id: 9,
    name: 'Sense',
    url: 'https://themes.shopify.com/themes/sense/styles/default',
    cartPage: 'compatible',
    cartDrawer: 'compatible',
  },
  {
    id: 10,
    name: 'Spotlight',
    url: 'https://themes.shopify.com/themes/spotlight/styles/default',
    cartPage: 'compatible',
    cartDrawer: 'not compatible',
  },
  {
    id: 11,
    name: 'Studio',
    url: 'https://themes.shopify.com/themes/studio/styles/default',
    cartPage: 'compatible',
    cartDrawer: 'compatible',
  },
  {
    id: 12,
    name: 'Taste',
    url: 'https://themes.shopify.com/themes/taste/styles/default',
    cartPage: 'compatible',
    cartDrawer: 'compatible',
  },
  {
    id: 13,
    name: 'Trade',
    url: 'https://themes.shopify.com/themes/trade/styles/default',
    cartPage: 'compatible',
    cartDrawer: 'compatible',
  },
];

const ModalDynamicThemeList = ({ isOpenModal, setIsOpenModal }: IProps) => {
  const renderStatus = (status?: Status) => {
    if (status === 'compatible') {
      return (
        <div className="d-flex justify-center">
          <Icon source={CheckIcon} />
        </div>
      );
    }
    if (status === 'not compatible') {
      return 'Not compatible';
    }
    return '-';
  };
  const rows = useMemo(() => {
    return themeList.map((item) => {
      return [
        <a key={item.id} className="d-flex" style={{ color: 'black' }} href={item.url} target="_blank" rel="noopener noreferrer">
          {item.name}
          <Icon source={ExternalIcon} />
        </a>,
        renderStatus(item.cartPage),
        renderStatus(item.cartDrawer),
        renderStatus(item.cartPopup),
      ];
    });
  }, []);
  return (
    <BaseModal
      title="Shopify themes compatible with dynamic date"
      open={isOpenModal}
      onClose={() => {
        setIsOpenModal(false);
      }}
      size="large"
    >
      <MediumText>
        Below are Shopify themes and cart types that are verified to be compatible with dynamic date mode. If you use another
        theme not listed here and are not sure about its compatibility,{' '}
        <Link monochrome onClick={openCrisp}>
          Contact us
        </Link>
      </MediumText>

      <ModalDynamicThemeListStyled className="mt-8">
        <LegacyCard>
          <DataTable
            columnContentTypes={['text', 'text', 'text', 'text']}
            headings={['Themes by Shopify', 'Cart page', 'Cart drawer', 'Cart popup']}
            rows={rows}
          />
        </LegacyCard>
      </ModalDynamicThemeListStyled>
    </BaseModal>
  );
};

export default ModalDynamicThemeList;
